import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Container,
  Paper,
  LinearProgress
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { RESET_PASSWORD_MUTATION } from '../graphql/mutations';

const ChangePassword = () => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const inputsRef = useRef([]);
  const [ChangePassword] = useMutation(RESET_PASSWORD_MUTATION);

  const phoneNumber = localStorage.getItem('mobile');

  useEffect(() => {
    if (inputsRef.current[0]) setTimeout(() => inputsRef.current[0].focus(), 100);
  }, []);

  useEffect(() => {
    if (!isOtpVerified && timer > 0) {
      const countdown = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(countdown);
    } else {
      setCanResend(true);
    }
  }, [timer, isOtpVerified]);

  const sendOtp = async () => {
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const mobile = localStorage.getItem('mobile');
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile }),
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuccessMsg('OTP sent successfully!');
        setTimer(30);
        setCanResend(false);
      } else {
        setErrorMsg(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      setErrorMsg('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    const newOtp = [...otp];

    if (value.length === 6) {
      value.split('').forEach((digit, i) => (newOtp[i] = digit));
      setOtp(newOtp);
      inputsRef.current[5]?.focus();
      return;
    }

    newOtp[index] = value[0] || '';
    setOtp(newOtp);
    if (value && index < 5) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const verifyOtp = async () => {
    if (!otp.every((d) => d)) {
      return setErrorMsg('Please enter the complete OTP.');
    }

    setLoading(true);
    setErrorMsg('');
    try {
      const response = await verifyOtpApi(otp.join(''));
      if (response.success) {
        setSuccessMsg('OTP verified successfully!');
        setTimeout(() => {
          setIsOtpVerified(true);
          setSuccessMsg('');
        }, 800);
      } else {
        setErrorMsg(response.message || 'OTP verification failed.');
      }
    } catch (err) {
      setErrorMsg('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = () => {
    setOtp(new Array(6).fill(''));
    sendOtp();
    inputsRef.current[0]?.focus();
  };

  const handleChangePassword = async () => {
    setErrorMsg('');
    if (newPassword !== confirmPassword) {
      return setErrorMsg('Passwords do not match.');
    }

    setLoading(true);
    try {
      const response = await changePasswordApi(newPassword);
      if (response.success) {
        setSuccessMsg('Password changed successfully!');
        setTimeout(() => {
          navigate('/login');
        }, 1200);
      } else {
        setErrorMsg(response.message || 'Password change failed.');
      }
    } catch (err) {
      setErrorMsg('Server error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpApi = async (otp) => {
    try {
      const mobile = localStorage.getItem('mobile');
      const response = await fetch('https://gcp-poc-with-apollo-1081525845464.asia-south1.run.app/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile, otp }),
      });

      const data = await response.json();

      if (data.status === 'error') {
        return { success: false, message: data.message || 'OTP verification failed.' };
      }

      return { success: true };
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const changePasswordApi = async (password) => {
    const id = localStorage.getItem('id');

    try {
      const { data } = await ChangePassword({
        variables: {
          Id: id,
          New_Password: password,
        },
      });

      return { success: true, message: data.changePassword };
    } catch (error) {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        'Network error. Please try again.';

      return { success: false, message };
    }
  };

  const theme = useTheme();

return (
  <Box
    sx={{
      minHeight: '100vh',
      backgroundColor: "#f8f9e9",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
    }}
  >
    <Container maxWidth="xs">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Paper elevation={4} sx={{ p: 4, borderRadius: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <LockResetIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Typography variant="h5" fontWeight={600} mt={1}>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Secure your account with a new password
            </Typography>
          </Box>

          <motion.div
            key={isOtpVerified ? 'password' : 'otp'}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {!isOtpVerified ? (
              <>
                <TextField
                  fullWidth
                  label="Mobile Number"
                  value={`+91 ${phoneNumber}`}
                  InputProps={{ readOnly: true }}
                  sx={{ mb: 3 }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={sendOtp}
                  disabled={loading}
                  sx={{ mb: 2 }}
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      value={digit}
                      onChange={(e) => handleOtpChange(e, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      inputRef={(el) => (inputsRef.current[index] = el)}
                      variant="outlined"
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: 'center', fontSize: '20px', padding: 10 },
                      }}
                      sx={{ width: 45 }}
                    />
                  ))}
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={((30 - timer) / 30) * 100}
                  sx={{ mb: 2, borderRadius: 2 }}
                />

                {errorMsg && (
                  <Typography color="error" align="center" sx={{ mb: 1 }}>
                    {errorMsg}
                  </Typography>
                )}
                {successMsg && (
                  <Typography color="success.main" align="center" sx={{ mb: 1 }}>
                    {successMsg}
                  </Typography>
                )}

                <Button fullWidth variant="contained" onClick={verifyOtp} disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </Button>

                <Button
                  fullWidth
                  onClick={resendOtp}
                  disabled={!canResend}
                  sx={{ mt: 1, color: canResend ? 'primary.main' : 'text.disabled' }}
                >
                  {canResend ? 'Resend OTP' : `Resend in ${timer}s`}
                </Button>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)}>
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    ),
                  }}
                />

                {errorMsg && (
                  <Typography color="error" align="center" sx={{ mb: 2 }}>
                    {errorMsg}
                  </Typography>
                )}
                {successMsg && (
                  <Typography color="success.main" align="center" sx={{ mb: 2 }}>
                    {successMsg}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Change Password'}
                </Button>
              </>
            )}
          </motion.div>
        </Paper>
      </motion.div>
    </Container>
  </Box>
);

};

export default ChangePassword;
